import { supabase } from './supabaseClient';

// Generate a unique referral code
export function generateReferralCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Create and assign a unique referral code to a user
export async function createReferralCode(userId: string): Promise<string | null> {
  let attempts = 0;
  const maxAttempts = 10;

  while (attempts < maxAttempts) {
    const code = generateReferralCode();
    
    // Try to update the user with this code
    const { error } = await supabase
      .from('users')
      .update({ referral_code: code })
      .eq('uid', userId);

    if (!error) {
      return code;
    }

    // If error is due to unique constraint, try again
    if (error.code === '23505') {
      attempts++;
      continue;
    }

    // Other error, return null
    console.error('Error creating referral code:', error);
    return null;
  }

  console.error('Failed to generate unique referral code after', maxAttempts, 'attempts');
  return null;
}

// Validate a referral code and return the referrer's info
export async function validateReferralCode(code: string) {
  if (!code || code.length !== 8) {
    return { valid: false, message: 'Invalid referral code format' };
  }

  const { data: referrer, error } = await supabase
    .from('users')
    .select('uid, email, referral_count, free_account_earned')
    .eq('referral_code', code.toUpperCase())
    .maybeSingle();

  if (error) {
    console.error('Error validating referral code:', error);
    return { valid: false, message: 'Error validating referral code' };
  }

  if (!referrer) {
    return { valid: false, message: 'Referral code not found' };
  }

  return { valid: true, referrer };
}

// Check if someone can refer others (prevent long chains)
export async function canRefer(userId: string): Promise<boolean> {
  const { data: user, error } = await supabase
    .from('users')
    .select('referred_by')
    .eq('uid', userId)
    .maybeSingle();

  if (error || !user) {
    return false;
  }

  // Allow referrals if:
  // User was not referred by someone else (prevents chains)
  // No subscription requirement - anyone can refer to grow the platform
  return !user.referred_by;
}

// Handle referral when someone makes a payment
export async function processReferralReward(userId: string) {
  // Get the user who just paid
  const { data: paidUser, error: paidUserError } = await supabase
    .from('users')
    .select('referred_by')
    .eq('uid', userId)
    .maybeSingle();

  if (paidUserError || !paidUser?.referred_by) {
    return; // User wasn't referred or error occurred
  }

  // Get the referrer
  const { data: referrer, error: referrerError } = await supabase
    .from('users')
    .select('uid, referral_count, free_account_earned, subscribed')
    .eq('uid', paidUser.referred_by)
    .maybeSingle();

  if (referrerError || !referrer) {
    console.error('Error getting referrer:', referrerError);
    return;
  }

  // Increment referrer's count
  const newCount = (referrer.referral_count || 0) + 1;

  // Check if they should get a free account (2 referrals and not already subscribed)
  const shouldGetFreeAccount = newCount >= 2 && !referrer.subscribed && !referrer.free_account_earned;

  const updatePayload: any = { referral_count: newCount };
  if (shouldGetFreeAccount) {
    updatePayload.free_account_earned = true;
    updatePayload.subscribed = true; // Give them access
  }

  const { error: updateError } = await supabase
    .from('users')
    .update(updatePayload)
    .eq('uid', referrer.uid);

  if (updateError) {
    console.error('Error updating referrer:', updateError);
  } else if (shouldGetFreeAccount) {
    console.log(`User ${referrer.uid} earned a free account through referrals!`);
  }
}

// Get referral stats for a user
export async function getReferralStats(userId: string) {
  const { data: user, error } = await supabase
    .from('users')
    .select('referral_code, referral_count, free_account_earned, referred_by')
    .eq('uid', userId)
    .maybeSingle();

  if (error) {
    console.error('Error getting referral stats:', error);
    return null;
  }

  // Get list of people they referred
  const { data: referrals, error: referralsError } = await supabase
    .from('users')
    .select('uid, email, subscribed, created_at')
    .eq('referred_by', userId);

  return {
    referralCode: user?.referral_code,
    referralCount: user?.referral_count || 0,
    freeAccountEarned: user?.free_account_earned || false,
    wasReferred: !!user?.referred_by,
    referredUsers: referrals || []
  };
} 